// src/contexts/AuthProvider.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { Spinner } from 'react-bootstrap';

import apiClient from '../services/api';
import { keycloakConfig } from '../utils/keycloak';

const AuthContext = createContext<{
  user: any;
  isAuthenticated: boolean;
} | null>(null);

// Answer 가 connector 콜백을 마친 뒤 심는 쿠키. 이게 있으면 Answer 세션이 있다.
// Keycloak 세션은 다른 도메인의 HttpOnly 쿠키라 여기서 읽을 수 없다. 그래서
// 이 쿠키만 보면 "이미 SSO 로그인한 사용자"를 알아볼 수 없고, 아래 무음 확인이 그 간격을 메운다.
function hasVisit() {
  return document.cookie.split('; ').some((c) => c.startsWith('visit='));
}

// 무음 확인에서 돌아왔음을 알리는 표시자, 그리고 탭 단위 1회 제한 키.
// 세션이 없을 때 Keycloak 과 무한 왕복하는 것을 막는다.
const PROBE_FLAG = 'sso_probe';
const PROBE_DONE_KEY = 'qurea_sso_probed';

// Keycloak 이 붙여 돌려주는 파라미터와 우리 표시자를 URL 에서 지운다.
function stripProbeParams(href: string) {
  const u = new URL(href);
  [
    PROBE_FLAG,
    'code',
    'error',
    'error_description',
    'iss',
    'session_state',
    'state',
  ].forEach((k) => u.searchParams.delete(k));
  return u.toString();
}

// prompt=none 무음 확인 URL.
//   세션 있음 → error 없이 즉시 돌아온다
//   세션 없음 → error=login_required 로 즉시 돌아온다
// 어느 쪽이든 로그인 화면이 뜨지 않으므로 비로그인 열람을 방해하지 않는다.
// response_type=none 을 쓰는 이유: 우리는 "세션이 있는가"만 알면 되고,
// qurea-portal 은 confidential client 라 프런트에서 코드를 교환할 수 없다.
// code 를 받아도 쓰지 못하고 버려지므로 애초에 발급받지 않는다.
function silentCheckUrl() {
  const base = keycloakConfig.url.replace(/\/$/, '');
  const redirect = new URL(stripProbeParams(window.location.href));
  redirect.searchParams.set(PROBE_FLAG, '1');
  const q = new URLSearchParams({
    client_id: keycloakConfig.clientId,
    response_type: 'none',
    scope: 'openid',
    prompt: 'none',
    redirect_uri: redirect.toString(),
  });
  return `${base}/realms/${keycloakConfig.realm}/protocol/openid-connect/auth?${q}`;
}

// Keycloak 세션이 확인된 뒤 Answer 로그인을 마무리하는 경로.
// 세션이 이미 있으니 로그인 화면 없이 통과한다.
function connectorLoginUrl() {
  const returnTo = stripProbeParams(window.location.href);
  return `/answer/api/v1/connector/login/basic?redirect_to=${encodeURIComponent(
    returnTo,
  )}`;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      try {
        // 1) Answer 세션이 있으면 사용자 정보만 조회하고 끝낸다
        if (hasVisit()) {
          const me = await apiClient.get('/answer/api/v1/user/info');
          setUser(me.data?.data ?? null);
          setIsLoading(false);
          return;
        }

        const params = new URLSearchParams(window.location.search);

        // 2) 무음 확인에서 돌아온 경우
        if (params.get(PROBE_FLAG)) {
          window.history.replaceState(
            {},
            '',
            stripProbeParams(window.location.href),
          );

          if (!params.get('error')) {
            // Keycloak 세션 있음 → connector 로 넘겨 Answer 로그인을 마무리한다
            window.location.assign(connectorLoginUrl());
            return; // 이동 중이므로 스피너를 유지한다
          }
          // login_required 등 → 비로그인으로 진행
          setUser(null);
          setIsLoading(false);
          return;
        }

        // 3) 이 탭에서 아직 확인하지 않았으면 무음 확인을 1회 한다.
        //    표시자를 떠나기 전에 먼저 심어, 돌아오지 못해도 반복되지 않게 한다.
        if (
          !sessionStorage.getItem(PROBE_DONE_KEY) &&
          keycloakConfig.url &&
          keycloakConfig.clientId
        ) {
          sessionStorage.setItem(PROBE_DONE_KEY, '1');
          window.location.assign(silentCheckUrl());
          return; // 이동 중이므로 스피너를 유지한다
        }

        // 4) 이미 확인했고 세션이 없었다 → 비로그인
        setUser(null);
        setIsLoading(false);
      } catch {
        setUser(null);
        setIsLoading(false);
      }
    };
    init();
  }, []);

  const contextValue = useMemo(
    () => ({ user, isAuthenticated: !!user }),
    [user],
  );

  if (isLoading)
    return (
      <div className="d-flex flex-column flex-grow-1 justify-content-center align-items-center">
        <Spinner variant="secondary" />
      </div>
    );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
